import {
  faCalendarPlus,
  faCalendarStar,
  faCog,
  faCoins,
  faCow,
  faEnvelope,
  faList,
  faClipboardListCheck,
  faPaintBrush,
  faTractor,
  faUserFriends,
  faUsers,
  faUsersCog,
  faWarehouse,
  faWheat,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import logo from '../../assets/logo.png';
import logo2x from '../../assets/logo@2x.png';
import {
  INTERNAL_ARTICLES_INDEX,
  INTERNAL_CLASSIFIERS_INDEX,
  INTERNAL_DASHBOARD,
  INTERNAL_DEVELOPMENT_SANDBOX,
  INTERNAL_EMAIL_TEMPLATE_INDEX,
  INTERNAL_EVENTS_INDEX,
  INTERNAL_ROLES_INDEX,
  INTERNAL_SYSTEM_PROPERTIES_INDEX,
  INTERNAL_USERS_INDEX,
  INTERNAL_AUDIT_LOGS_INDEX,
  INTERNAL_HORIZON,
} from '../../router/routes';
import { can, route } from '../../services/utils';
import css from './sidebar.module.css';
import VisibleInDevelopment from '../development/visible-in-development';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { ArticleStatus } from '../../enums/article-status';
import { EventStatus } from '../../enums/event-status';
import { FarmUser } from '../../interfaces/farm-user';

const menu = [
  {
    label: 'Administration',
    items: [
      {
        label: 'Users',
        icon: faUsers,
        link: INTERNAL_USERS_INDEX,
        ability: { entity_type: 'user' },
      },
      {
        label: 'Roles',
        icon: faUsersCog,
        link: INTERNAL_ROLES_INDEX,
        ability: { entity_type: 'role' },
      },
      {
        label: 'pages.internal.articles.menu.title',
        icon: faCalendarPlus,
        link: route(INTERNAL_ARTICLES_INDEX.concat('?status=:status'), {
          status: ArticleStatus.PUBLIC,
        }),
        ability: { entity_type: 'article' },
      },
      {
        label: 'pages.internal.events.menu.title',
        icon: faCalendarStar,
        link: route(INTERNAL_EVENTS_INDEX.concat('?status=:status'), {
          status: EventStatus.PUBLIC,
        }),
        ability: { entity_type: 'event' },
      },
      {
        label: 'Audit logs',
        icon: faList,
        link: INTERNAL_AUDIT_LOGS_INDEX,
        ability: { entity_type: 'audit' },
      },
      {
        label: 'Queue status',
        icon: faClipboardListCheck,
        link: INTERNAL_HORIZON,
        target: '_blank',
        ability: { name: 'View queue status' },
      },
    ],
  },
  {
    label: 'Configuration',
    items: [
      {
        label: 'Warehouse classifiers',
        icon: faWarehouse,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'warehouse',
        }),
        ability: { name: 'Manage warehouse classifiers' },
      },
      {
        label: 'Crop classifiers',
        icon: faWheat,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'crop',
        }),
        ability: { name: 'Manage crop classifiers' },
      },
      {
        label: 'Livestock classifiers',
        icon: faCow,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'livestock',
        }),
        ability: { name: 'Manage livestock classifiers' },
      },
      {
        label: 'Equipment classifiers',
        icon: faTractor,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'equipment',
        }),
        ability: { name: 'Manage equipment classifiers' },
      },
      {
        label: 'Human classifiers',
        icon: faUserFriends,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'human',
        }),
        ability: { name: 'Manage human classifiers' },
      },
      {
        label: 'Finance classifiers',
        icon: faCoins,
        link: route(INTERNAL_CLASSIFIERS_INDEX, {
          groupKey: 'finance',
        }),
        ability: { name: 'Manage finance classifiers' },
      },
      {
        label: 'System settings',
        icon: faCog,
        link: route(INTERNAL_SYSTEM_PROPERTIES_INDEX),
        ability: { entity_type: 'system_configuration' },
      },
      {
        label: 'Email templates',
        icon: faEnvelope,
        link: route(INTERNAL_EMAIL_TEMPLATE_INDEX),
        ability: { entity_type: 'email_template' },
      },
    ],
  },
];

export function buildMenu(farmUser: FarmUser) {
  return menu
    .map((item) => {
      const builtItems = item.items
        .map((subItems) => {
          return can(farmUser, subItems.ability) ? subItems : null;
        })
        .filter(Boolean);

      return builtItems.length ? { ...item, items: builtItems } : null;
    })
    .filter(Boolean);
}

const Sidebar = () => {
  const { t } = useTranslation();
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const [builtMenu, setBuiltMenu] = useState([]);
  useEffect(() => {
    setBuiltMenu(buildMenu(farmUser));
  }, [farmUser, setBuiltMenu]);

  return (
    <div className={css['sidebar']}>
      <div className={css['logo']}>
        <Link to={INTERNAL_DASHBOARD}>
          <img
            src={logo}
            srcSet={logo + ' 1x,' + logo2x + ' 2x'}
            alt="Mans Lauks"
          />
        </Link>
      </div>
      <div className={css['menu']}>
        <ul>
          {builtMenu.map((group, index) => (
            <li key={index}>
              <div className={css['title']}>{t(group.label)}</div>
              <ul className={css['items']}>
                {group.items.map((item: any, index: number) => (
                  <li key={index}>
                    <Link to={item.link} target={item.target}>
                      <FontAwesomeIcon icon={item.icon} />
                      {t(item.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          <DevelopmentSandboxMenuEntry />
        </ul>
      </div>
    </div>
  );
};

/**
 * Link for opening the development sandbox.
 * It is separate from the regular menu structure, it will only be available in development mode (locally).
 */
function DevelopmentSandboxMenuEntry() {
  const developmentGroupTitle = 'Development (local only)';
  const menuEntries = [
    {
      key: 'sandbox',
      title: 'Components', // no translation necessary
      icon: faPaintBrush,
      link: INTERNAL_DEVELOPMENT_SANDBOX,
    },
  ];
  return (
    <VisibleInDevelopment>
      <li key={'development'}>
        <div className={css['title']}>{developmentGroupTitle}</div>
        <ul className={css['items']}>
          {menuEntries.map((menuEntry) => (
            <li key={menuEntry.key}>
              <NavLink to={menuEntry.link}>
                <FontAwesomeIcon icon={menuEntry.icon} />
                <span>{menuEntry.title}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    </VisibleInDevelopment>
  );
}

export default Sidebar;
